/* Mobile filter island controller.

   - Filter button: programmatically clicks the existing
     <menu-drawer> mobile-facets summary so the native drawer
     open/close pipeline runs (overlay, focus trap, body lock).
   - Sort button: opens an in-house bottom sheet.
   - View buttons: handled by the existing <view-switcher> custom
     element rendered inside the island.
*/

(() => {
  if (customElements.get('mobile-filter-island')) return;

  const SHEET_OPEN = 'is-open';
  const BODY_LOCK = 'mfi-sheet-open';

  class MobileFilterIsland extends HTMLElement {
    connectedCallback() {
      this.filterBtn = this.querySelector('.js-mfi-filter');
      this.sortBtn = this.querySelector('.js-mfi-sort');

      if (this.filterBtn) {
        this.filterBtn.addEventListener('click', this.handleFilterClick.bind(this));
      }
      if (this.sortBtn) {
        this.sortBtn.addEventListener('click', () => this.openSheet('mfi-sort-sheet', this.sortBtn));
      }

      document.querySelectorAll('.mfi-sheet').forEach((sheet) => this.bindSheet(sheet));

      // Close X inside the mobile-facets drawer header. Delegate to the
      // same close path the filter button uses so the menu-drawer's
      // animation, focus trap, and body lock all unwind correctly.
      document.querySelectorAll('.mfi-drawer-close').forEach((btn) => {
        btn.addEventListener('click', () => this.closeDrawer());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const openSheet = document.querySelector('.mfi-sheet.' + SHEET_OPEN);
        if (openSheet) this.closeSheet(openSheet);
      });

      this.setupFilterCountSync();
    }

    setupFilterCountSync() {
      // facets.js refreshes .mobile-facets__open (which holds the existing
      // theme count-bubble) on every AJAX filter change, but doesn't touch
      // this island. Mirror that bubble's value into our badge.
      const badge = this.querySelector('[data-mfi-filter-count]');
      if (!badge) return;

      const readCount = () => {
        const bubble = document.querySelector('.mobile-facets__open .count-bubble');
        if (!bubble) return 0;
        const n = parseInt((bubble.textContent || '').trim(), 10);
        return isNaN(n) ? 0 : n;
      };

      const apply = () => {
        const count = readCount();
        if (count > 0) {
          badge.textContent = String(count);
          badge.removeAttribute('hidden');
        } else {
          badge.setAttribute('hidden', '');
          badge.textContent = '0';
        }
      };

      apply();

      const target = document.querySelector('.mobile-facets__wrapper');
      if (target && 'MutationObserver' in window) {
        const obs = new MutationObserver(apply);
        obs.observe(target, { childList: true, subtree: true, characterData: true });
      }
    }

    closeDrawer() {
      const wrapper = document.querySelector('.mobile-facets__wrapper');
      if (!wrapper) return;
      if (typeof wrapper.closeMenuDrawer === 'function') {
        wrapper.closeMenuDrawer({}, this.filterBtn);
      } else {
        const details = wrapper.querySelector('.disclosure-has-popup');
        if (details) {
          details.open = false;
          details.classList.remove('menu-opening');
        }
      }
      if (this.filterBtn) this.filterBtn.setAttribute('aria-expanded', 'false');
    }

    handleFilterClick(e) {
      e.preventDefault();
      // The existing <summary> is display:none at this breakpoint, so
      // clicking it doesn't trigger the browser's native details toggle.
      // We do everything explicitly: open the details, then call the
      // menu-drawer custom element's openMenuDrawer() so its animation
      // class, focus trap, and body scroll lock all apply.
      const wrapper = document.querySelector('.mobile-facets__wrapper');
      const details = wrapper && wrapper.querySelector('.disclosure-has-popup');
      const summary = details && details.querySelector('summary');
      if (!wrapper || !details || !summary) return;

      if (details.open && wrapper.classList.contains('menu-opening') || details.classList.contains('menu-opening')) {
        // closing path — let the menu-drawer's own close handler run
        if (typeof wrapper.closeMenuDrawer === 'function') {
          wrapper.closeMenuDrawer({}, this.filterBtn);
        } else {
          details.open = false;
          details.classList.remove('menu-opening');
        }
        this.filterBtn.setAttribute('aria-expanded', 'false');
      } else {
        details.open = true;
        if (typeof wrapper.openMenuDrawer === 'function') {
          wrapper.openMenuDrawer(summary);
        } else {
          details.classList.add('menu-opening');
        }
        this.filterBtn.setAttribute('aria-expanded', 'true');
      }
    }

    bindSheet(sheet) {
      sheet.querySelectorAll('[data-mfi-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeSheet(sheet));
      });
      sheet.querySelectorAll('[data-mfi-sort-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.applySort(btn.dataset.mfiSortValue);
          this.closeSheet(sheet);
        });
      });
    }

    openSheet(id, triggerBtn) {
      const sheet = document.getElementById(id);
      if (!sheet) return;
      sheet.classList.add(SHEET_OPEN);
      sheet.setAttribute('aria-hidden', 'false');
      document.body.classList.add(BODY_LOCK);
      if (triggerBtn) triggerBtn.setAttribute('aria-expanded', 'true');
      this._activeTrigger = triggerBtn || null;

      const focusable = sheet.querySelector('.mfi-sheet__option, .mfi-sheet__close');
      if (focusable) focusable.focus({ preventScroll: true });
    }

    closeSheet(sheet) {
      sheet.classList.remove(SHEET_OPEN);
      sheet.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(BODY_LOCK);

      if (this.sortBtn) this.sortBtn.setAttribute('aria-expanded', 'false');

      if (this._activeTrigger) {
        this._activeTrigger.focus({ preventScroll: true });
        this._activeTrigger = null;
      }
    }

    applySort(value) {
      if (!value) return;
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', value);
      window.location.href = url.toString();
    }
  }

  customElements.define('mobile-filter-island', MobileFilterIsland);
})();
